import { theme, Typography } from 'antd';
import { Column, Line } from '@ant-design/charts';
import type { CSSProperties } from 'react';
import type { SerieMensual } from '@aid/shared';
import { EmptyState } from '../../../components/EmptyState';
import { formatCurrency, getCurrencySymbol } from '../../../lib/format';
import { DashboardCard } from '../../../components/DashboardCard';
import { monthAxisLabel, formatYAxisCompact, tooltipRowHTML } from '../../../lib/chart-utils';
import { useT } from '../../../locales';
import './SerieMensualChart.css';

const { Text } = Typography;

const COLLECTION_CHART_HEIGHT = 220;
const MORA_CHART_HEIGHT = 112;

interface MonthlyChartDatum {
  mes: string;
  serie: string;
  value: number;
}

interface MoraChartDatum {
  mes: string;
  mora: number;
}

interface TooltipDatum {
  serie?: string;
  value?: number;
  mora?: number;
}

interface TooltipItem {
  data?: TooltipDatum;
  serie?: string;
  name?: string;
  value?: number;
}

interface TooltipContext {
  title: string;
  items: TooltipItem[];
}

interface SummaryMetricProps {
  label: string;
  value: string;
  color: string;
  labelColor: string;
  testId: string;
}

interface SerieMensualChartProps {
  serie: SerieMensual[];
  fechaCorte: string;
}

function safeNum(val: string): number {
  const n = Number.parseFloat(val);
  return Number.isFinite(n) ? n : 0;
}

function finiteValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function tooltipSeries(item: TooltipItem): string {
  return item.data?.serie ?? item.serie ?? item.name ?? '';
}

function tooltipValue(item: TooltipItem): number {
  return finiteValue(item.data?.value ?? item.value);
}

function tooltipMoraValue(item: TooltipItem): number {
  return finiteValue(item.data?.mora ?? item.data?.value ?? item.value);
}

function formatCollectionRatio(facturado: number, recaudado: number): string {
  if (facturado <= 0) return '—';
  const percentage = (recaudado / facturado) * 100;
  const rounded = percentage >= 10 ? Math.round(percentage) : Math.round(percentage * 10) / 10;
  return `${rounded}%`;
}

function SummaryMetric({ label, value, color, labelColor, testId }: SummaryMetricProps) {
  return (
    <div
      className="monthly-summary__metric"
      data-testid={testId}
      role="group"
      aria-label={`${label}: ${value}`}
    >
      <div className="monthly-summary__metric-label">
        <span
          className="monthly-summary__metric-dot"
          aria-hidden
          style={{ backgroundColor: color }}
        />
        <Text type="secondary" style={{ color: labelColor }}>
          {label}
        </Text>
      </div>
      <Text
        strong
        style={{ color, fontSize: 18, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>
    </div>
  );
}

export function SerieMensualChart({ serie, fechaCorte }: SerieMensualChartProps) {
  const t = useT();
  const { token } = theme.useToken();
  const symbol = getCurrencySymbol();

  const facturadoLabel = t('dashboard.serieFacturado');
  const recaudadoLabel = t('dashboard.serieRecaudado');
  const moraLabel = t('dashboard.serieMora');

  const hasData = serie.some(
    (s) => safeNum(s.facturado) > 0 || safeNum(s.recaudado) > 0 || safeNum(s.mora) > 0,
  );

  const latest = serie.length > 0 ? serie[serie.length - 1] : undefined;
  const latestFacturado = latest ? safeNum(latest.facturado) : 0;
  const latestRecaudado = latest ? safeNum(latest.recaudado) : 0;
  const latestRatio = formatCollectionRatio(latestFacturado, latestRecaudado);
  const paymentSemanticsNote = (
    <Text
      type="secondary"
      className="monthly-summary__note"
      style={{ display: 'block', marginTop: 8, fontSize: 12, lineHeight: 1.4 }}
    >
      {t('dashboard.seriePagosNota')}
    </Text>
  );

  const collectionData: MonthlyChartDatum[] = serie.flatMap((s) => {
    const mes = monthAxisLabel(s.mes);
    return [
      { mes, serie: facturadoLabel, value: safeNum(s.facturado) },
      { mes, serie: recaudadoLabel, value: safeNum(s.recaudado) },
    ];
  });

  const moraData: MoraChartDatum[] = serie.map((s) => ({
    mes: monthAxisLabel(s.mes),
    mora: safeNum(s.mora),
  }));

  const collectionTooltipRender = (_event: unknown, context: TooltipContext) => {
    const bySerie = new Map(context.items.map((item) => [tooltipSeries(item), tooltipValue(item)]));
    const facturado = bySerie.get(facturadoLabel) ?? 0;
    const recaudado = bySerie.get(recaudadoLabel) ?? 0;
    const ratio = formatCollectionRatio(facturado, recaudado);

    return `
      <div style="min-width:200px;margin:-12px;padding:12px;background:${token.colorBgElevated};border-radius:4px;">
        <div style="font-weight:600;font-size:12px;color:${token.colorText};margin-bottom:6px;">${context.title}</div>
        ${tooltipRowHTML(facturadoLabel, facturado, token.colorPrimary, token)}
        ${tooltipRowHTML(recaudadoLabel, recaudado, token.colorSuccess, token)}
        <div style="border-top:1px solid ${token.colorSplit};margin-top:6px;padding-top:6px;font-size:12px;color:${token.colorTextSecondary};">
          ${t('dashboard.serieEficiencia')}: <strong style="color:${token.colorText};font-variant-numeric:tabular-nums;">${ratio}</strong>
        </div>
      </div>`;
  };

  const moraTooltipRender = (_event: unknown, context: TooltipContext) => {
    const mora = tooltipMoraValue(context.items[0] ?? {});
    return `
      <div style="min-width:180px;margin:-12px;padding:12px;background:${token.colorBgElevated};border-radius:4px;">
        <div style="font-weight:600;font-size:12px;color:${token.colorText};margin-bottom:6px;">${context.title}</div>
        ${tooltipRowHTML(moraLabel, mora, token.colorError, token)}
      </div>`;
  };

  return (
    <DashboardCard
      style={{ height: '100%' }}
      bodyStyle={{ padding: '18px 22px 24px' }}
      title={t('dashboard.serieMensualTitle')}
      subtitle={t('dashboard.serieMensualSubtitle')}
    >
      {!hasData ? (
        <>
          <Text
            type="secondary"
            className="monthly-summary__period"
            style={{ display: 'block', marginBottom: 12, color: token.colorText }}
          >
            {t('dashboard.mesEnCursoCorte', { fecha: fechaCorte })}
          </Text>
          <EmptyState
            compact
            title={t('dashboard.serieSinDatos')}
            description={t('dashboard.serieSinDatosDesc')}
          />
          {paymentSemanticsNote}
        </>
      ) : (
        <>
          <div
            className="monthly-summary"
            aria-label={t('dashboard.mesEnCursoCorte', { fecha: fechaCorte })}
          >
            <Text
              type="secondary"
              className="monthly-summary__period"
              style={{ color: token.colorText }}
            >
              {t('dashboard.mesEnCursoCorte', { fecha: fechaCorte })}
            </Text>
            <div className="monthly-summary__metrics">
              <SummaryMetric
                label={facturadoLabel}
                value={formatCurrency(latestFacturado.toFixed(2))}
                color={token.colorPrimary}
                labelColor={token.colorText}
                testId="monthly-kpi-facturado"
              />
              <SummaryMetric
                label={recaudadoLabel}
                value={formatCurrency(latestRecaudado.toFixed(2))}
                color={token.colorSuccess}
                labelColor={token.colorText}
                testId="monthly-kpi-recaudado"
              />
              <SummaryMetric
                label={t('dashboard.serieEficiencia')}
                value={latestRatio}
                color={token.colorInfo}
                labelColor={token.colorText}
                testId="monthly-kpi-eficiencia"
              />
            </div>
          </div>
          {paymentSemanticsNote}

          <div className="monthly-visuals">
            <section className="monthly-visual" aria-label={t('dashboard.serieFacturacionSection')}>
              <div className="monthly-section-heading">
                <Text strong>{t('dashboard.serieFacturacionSection')}</Text>
                <Text type="secondary" style={{ color: token.colorText }}>
                  {t('dashboard.serieFacturacionSectionDesc')}
                </Text>
              </div>
              <div
                role="img"
                aria-label={t('dashboard.serieFacturacionAria')}
                className="monthly-chart"
              >
                <Column
                  data={collectionData}
                  xField="mes"
                  yField="value"
                  colorField="serie"
                  group
                  color={[token.colorPrimary, token.colorSuccess]}
                  scale={{
                    color: {
                      domain: [facturadoLabel, recaudadoLabel],
                      range: [token.colorPrimary, token.colorSuccess],
                    },
                  }}
                  height={COLLECTION_CHART_HEIGHT}
                  axis={{
                    y: {
                      labelFormatter: (v: number) => formatYAxisCompact(v, symbol),
                      style: {
                        labelFill: token.colorText,
                        gridStroke: token.colorSplit,
                      },
                    },
                    x: {
                      style: {
                        labelFill: token.colorText,
                        lineStroke: token.colorBorderSecondary,
                        tickStroke: token.colorBorderSecondary,
                      },
                    },
                  }}
                  legend={{
                    color: {
                      position: 'top',
                      layout: { justifyContent: 'center' },
                      itemLabelFill: token.colorText,
                    },
                  }}
                  tooltip={{
                    title: (d: { mes: string }) => d.mes,
                  }}
                  interaction={{
                    tooltip: { render: collectionTooltipRender },
                  }}
                />
              </div>
            </section>

            <section
              className="monthly-visual monthly-visual--mora"
              style={{ '--monthly-border': token.colorBorderSecondary } as CSSProperties}
              aria-label={moraLabel}
            >
              <div className="monthly-section-heading monthly-section-heading--mora">
                <div>
                  <Text strong>{moraLabel}</Text>
                  <Text type="secondary" style={{ color: token.colorText }}>
                    {t('dashboard.serieMoraSubtitle')}
                  </Text>
                </div>
              </div>
              <div role="img" aria-label={t('dashboard.serieMoraAria')} className="monthly-chart">
                <Line
                  data={moraData}
                  xField="mes"
                  yField="mora"
                  color={token.colorError}
                  style={{ stroke: token.colorErrorHover, lineWidth: 2 }}
                  height={MORA_CHART_HEIGHT}
                  axis={{
                    y: {
                      labelFormatter: (v: number) => formatYAxisCompact(v, symbol),
                      style: {
                        labelFill: token.colorText,
                        gridStroke: token.colorSplit,
                      },
                    },
                    x: {
                      style: {
                        labelFill: token.colorText,
                        lineStroke: token.colorBorderSecondary,
                        tickStroke: token.colorBorderSecondary,
                      },
                    },
                  }}
                  tooltip={{
                    title: (d: { mes: string }) => d.mes,
                  }}
                  interaction={{
                    tooltip: { render: moraTooltipRender },
                  }}
                />
              </div>
            </section>
          </div>
        </>
      )}
    </DashboardCard>
  );
}
